import { useState, useEffect } from "react";
import { CT } from "@/content";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { 
  GraduationCap, 
  BookOpen, 
  ClipboardCheck, 
  Users,
  FileText,
  Plus,
  LogOut,
  User,
  ChevronRight,
  Copy,
  Check,
  Pencil,
  Zap,
  Swords,
  Trophy,
  RotateCcw,
  ArrowLeft,
  UserPlus,
  Coins,
  Flag
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown } from "lucide-react";
import { NotificationBell } from "../components/NotificationBell";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { motion } from "framer-motion";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  usePushNotifications(token);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameClass, setRenameClass] = useState(null);
  const [showArena, setShowArena] = useState(false);
  // Arena state
  const [battleTables, setBattleTables] = useState([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
  const [battleTitle, setBattleTitle] = useState("");
  const [battleDuration, setBattleDuration] = useState(3);
  const [selectedBattleClasses, setSelectedBattleClasses] = useState([]);
  const [classesPickerOpen, setClassesPickerOpen] = useState(false);
  const [creatingBattle, setCreatingBattle] = useState(false);
  const [activeBattles, setActiveBattles] = useState([]);
  const [battleHistory, setBattleHistory] = useState([]);
  const [viewingBattle, setViewingBattle] = useState(null);
  const [weeklyBoards, setWeeklyBoards] = useState({});
  const [renameData, setRenameData] = useState({ name: "", description: "", subject: "" });
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showPlayers, setShowPlayers] = useState(false);
  const [players, setPlayers] = useState(null);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [upgradeClassId, setUpgradeClassId] = useState("");
  const [upgradePassword, setUpgradePassword] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [reports, setReports] = useState(null);
  const [newClass, setNewClass] = useState({
    name: "",
    description: "",
    subject: "",
    schedule: "",
    meet_link: ""
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, statsRes] = await Promise.all([
        axios.get(`${API}/classes`, { headers }),
        axios.get(`${API}/dashboard/stats`, { headers })
      ]);
      
      setClasses(classesRes.data);
      setStats(statsRes.data);
      fetchArenaData(classesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArenaData = async (classList) => {
    const cl = classList || classes;
    if (!cl.length) return;
    try {
      const activePromises = cl.map(c => axios.get(`${API}/arena/class-battle/active/${c.class_id}`, { headers }).catch(() => ({ data: [] })));
      const historyPromises = cl.map(c => axios.get(`${API}/arena/class-battle/history/${c.class_id}`, { headers }).catch(() => ({ data: [] })));
      const [activeResults, historyResults] = await Promise.all([Promise.all(activePromises), Promise.all(historyPromises)]);
      setActiveBattles(activeResults.flatMap(r => r.data || []));
      setBattleHistory(historyResults.flatMap(r => r.data || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 10));
    } catch {}
  };

  const toggleBattleTable = (t) => setBattleTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a, b) => a - b));

  const toggleBattleClass = (cid) => setSelectedBattleClasses(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  const toggleAllBattleClasses = () => setSelectedBattleClasses(prev => prev.length === classes.length ? [] : classes.map(c => c.class_id));

  const createClassBattle = async () => {
    if (battleTables.length === 0) { toast.error("Select at least one table"); return; }
    if (selectedBattleClasses.length === 0) { toast.error("Select at least one class"); return; }
    setCreatingBattle(true);
    try {
      const res = await axios.post(`${API}/arena/class-battle/create`, {
        class_ids: selectedBattleClasses,
        tables: battleTables,
        title: battleTitle || "Class Battle",
        duration_minutes: battleDuration
      }, { headers });
      const count = res.data?.count ?? selectedBattleClasses.length;
      toast.success(count > 1 ? `Battle started for ${count} classes!` : "Class Battle created!");
      setBattleTitle("");
      fetchArenaData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setCreatingBattle(false);
  };

  const endBattle = async (battleId) => {
    if (!confirm("End this battle?")) return;
    try {
      await axios.post(`${API}/arena/class-battle/${battleId}/end`, {}, { headers });
      toast.success("Battle ended");
      fetchArenaData();
    } catch { toast.error("Failed"); }
  };

  const viewBattleResults = async (battleId) => {
    try {
      const res = await axios.get(`${API}/arena/class-battle/${battleId}`, { headers });
      setViewingBattle(res.data);
    } catch { toast.error("Failed to load results"); }
  };

  const handleCreateClass = async () => {
    if (!newClass.name || !newClass.subject) {
      toast.error("Please fill in required fields");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/classes`,
        newClass,
        { headers }
      );
      toast.success("Class created successfully!");
      setNewClass({ name: "", description: "", subject: "", schedule: "", meet_link: "" });
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create class");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Join code copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const openRenameDialog = (cls) => {
    setRenameClass(cls);
    setRenameData({ name: cls.name, description: cls.description || "", subject: cls.subject || "" });
    setRenameDialogOpen(true);
  };

  const handleRenameClass = async () => {
    if (!renameData.name.trim()) {
      toast.error("Class name is required");
      return;
    }
    setRenaming(true);
    try {
      await axios.put(`${API}/classes/${renameClass.class_id}/settings`, renameData, { headers });
      toast.success("Class updated successfully!");
      setRenameDialogOpen(false);
      setRenameClass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update class");
    } finally {
      setRenaming(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="teacher-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="TheMatheMind 360" className="h-10 rounded-lg" />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-foreground font-['Manrope'] leading-tight">TheMatheMind <span className="text-amber-600">360</span></span>
                <span className="text-[10px] text-muted-foreground leading-tight tracking-wider uppercase">A 360&deg; Learning Platform</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <NotificationBell token={token} />
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{user?.name}</span>
                <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">Teacher</span>
              </div>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground font-['Manrope']">
            Teacher Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Manage your classes and students</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_classes || 0}</p>
                    <p className="text-xs text-muted-foreground">Classes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_students || 0}</p>
                    <p className="text-xs text-muted-foreground">Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_quizzes || 0}</p>
                    <p className="text-xs text-muted-foreground">Quizzes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_documents || 0}</p>
                    <p className="text-xs text-muted-foreground">Documents</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>


        {/* Arena Players — upgrade to students */}
        <Card className="border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 cursor-pointer hover:shadow-lg transition-all mb-6"
          onClick={async () => {
            setShowPlayers(true);
            try {
              const r = await axios.get(`${API}/arena/players`, { headers: { Authorization: `Bearer ${token}` } });
              setPlayers(r.data || []);
            } catch (e) { setPlayers([]); }
          }} data-testid="arena-players-card">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shrink-0">
              <UserPlus className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold font-['Manrope']">Arena Players</h3>
              <p className="text-sm text-muted-foreground">Upgrade guest Arena players into enrolled students — their coins come with them</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </CardContent>
        </Card>

        {/* Reported Problems — from Olympiad exams */}
        <Card className="border-rose-500/20 bg-gradient-to-r from-rose-500/5 to-orange-500/5 cursor-pointer hover:shadow-lg transition-all mb-6"
          onClick={async () => {
            setShowReports(true);
            try {
              const r = await axios.get(`${API}/secure-exam-reports`, { headers: { Authorization: `Bearer ${token}` } });
              setReports(r.data.reports || []);
            } catch (e) { setReports([]); }
          }} data-testid="question-reports-card">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0">
              <Flag className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold font-['Manrope']"><CT k="teacher_reports_title" /></h3>
              <CT k="teacher_reports_subtitle" block className="text-sm text-muted-foreground" />
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </CardContent>
        </Card>

        {/* Times Tables Arena Section */}
        {!showArena ? (
          <Card className="border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-pink-500/5 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => { setShowArena(true); fetchArenaData(); }} data-testid="arena-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold font-['Manrope']">Times Tables Arena</h3>
                <p className="text-sm text-muted-foreground">Create class battles, view leaderboards, assign focus tables</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </CardContent>
          </Card>
        ) : viewingBattle ? (
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setViewingBattle(null)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
                <CardTitle className="font-['Manrope'] text-base">{viewingBattle.title}</CardTitle>
                <span className={`text-xs px-2 py-0.5 rounded-full ${viewingBattle.status === "active" ? "bg-green-500/10 text-green-500" : "bg-muted text-muted-foreground"}`}>{viewingBattle.status}</span>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{(viewingBattle.participants || []).length} participants &middot; Tables: {(viewingBattle.tables || []).join(", ")}x</p>
              {(viewingBattle.participants || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-6">No submissions yet</p>
              ) : (
                <div className="space-y-2">
                  {(viewingBattle.participants || []).map((p, i) => (
                    <div key={p.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-300" : i === 2 ? "bg-orange-400 text-white" : "bg-muted"}`}>{i + 1}</span>
                      <div className="flex-1"><p className="font-medium text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{p.answered} answered &middot; {p.avg_time_ms ? `${(p.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p></div>
                      <p className="text-lg font-bold text-green-500">{p.score}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="font-['Manrope'] flex items-center gap-2"><Zap className="w-5 h-5 text-purple-500" />Times Tables Arena</CardTitle>
                <CardDescription>Create battles and manage arena for your classes</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowArena(false)}>Minimize</Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Create Class Battle */}
              <div className="p-4 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-3">
                <h4 className="font-bold text-sm flex items-center gap-1"><Swords className="w-4 h-4 text-purple-500" />Create Class Battle</h4>
                <input type="text" value={battleTitle} onChange={e => setBattleTitle(e.target.value)} placeholder="Battle title (e.g., Friday Speed Test)" data-testid="arena-battle-title"
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Classes:</p>
                  <Popover open={classesPickerOpen} onOpenChange={setClassesPickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="arena-classes-picker-trigger"
                        className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm flex items-center justify-between hover:bg-accent/50 transition-colors"
                      >
                        <span className="truncate text-left">
                          {selectedBattleClasses.length === 0
                            ? "Select classes..."
                            : selectedBattleClasses.length === classes.length
                            ? `All Classes (${classes.length})`
                            : selectedBattleClasses.length === 1
                            ? classes.find(c => c.class_id === selectedBattleClasses[0])?.name || "1 class"
                            : `${selectedBattleClasses.length} classes selected`}
                        </span>
                        <ChevronDown className="w-4 h-4 opacity-60 shrink-0 ml-2" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <div className="max-h-64 overflow-y-auto">
                        <button
                          type="button"
                          onClick={toggleAllBattleClasses}
                          data-testid="arena-classes-toggle-all"
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b border-border hover:bg-accent/50 transition-colors"
                        >
                          <Checkbox
                            checked={classes.length > 0 && selectedBattleClasses.length === classes.length}
                            className="pointer-events-none"
                          />
                          <span>All Classes ({classes.length})</span>
                        </button>
                        {classes.map(c => (
                          <button
                            key={c.class_id}
                            type="button"
                            onClick={() => toggleBattleClass(c.class_id)}
                            data-testid={`arena-class-opt-${c.class_id}`}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50 transition-colors"
                          >
                            <Checkbox
                              checked={selectedBattleClasses.includes(c.class_id)}
                              className="pointer-events-none"
                            />
                            <span className="truncate text-left">{c.name}</span>
                          </button>
                        ))}
                        {classes.length === 0 && (
                          <div className="px-3 py-4 text-xs text-muted-foreground text-center">No classes yet</div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Tables:</p>
                  <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5">
                    {[2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(t => (
                      <button key={t} onClick={() => toggleBattleTable(t)} data-testid={`arena-bt-${t}`}
                        className={`h-9 rounded-lg font-bold text-xs transition-all ${battleTables.includes(t) ? "bg-purple-500 text-white" : "bg-muted text-muted-foreground"}`}>{t}x</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Duration:</span>
                  {[2, 3, 5].map(d => (
                    <button key={d} onClick={() => setBattleDuration(d)} data-testid={`arena-dur-${d}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${battleDuration === d ? "bg-purple-500 text-white" : "bg-muted text-muted-foreground"}`}>{d} min</button>
                  ))}
                </div>
                <Button onClick={createClassBattle} disabled={creatingBattle || battleTables.length === 0 || selectedBattleClasses.length === 0}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:opacity-90" data-testid="arena-create-btn">
                  <Swords className="w-4 h-4 mr-2" />{creatingBattle ? "Creating..." : selectedBattleClasses.length === 0 ? "Select Classes" : selectedBattleClasses.length === classes.length ? `Start for All ${classes.length} Classes` : selectedBattleClasses.length > 1 ? `Start for ${selectedBattleClasses.length} Classes` : "Start Class Battle"}
                </Button>
              </div>

              {/* Active Battles */}
              {activeBattles.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-bold text-sm flex items-center gap-1"><Zap className="w-4 h-4 text-green-500" />Active Battles ({activeBattles.length})</h4>
                  {activeBattles.map(b => (
                    <div key={b.battle_id} className="flex items-center justify-between p-3 rounded-xl bg-green-500/5 border border-green-500/20">
                      <div>
                        <p className="font-medium text-sm">{b.title}</p>
                        <p className="text-xs text-muted-foreground">{b.class_name} &middot; {b.duration_minutes} min</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => viewBattleResults(b.battle_id)}>Results</Button>
                        <Button size="sm" variant="destructive" onClick={() => endBattle(b.battle_id)}>End</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Battle History */}
              {battleHistory.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-bold text-sm flex items-center gap-1"><Trophy className="w-4 h-4" />Recent Battles</h4>
                  {battleHistory.slice(0, 5).map(b => (
                    <div key={b.battle_id} className="p-3 rounded-xl bg-muted/30 cursor-pointer hover:bg-muted/50 transition-all" onClick={() => viewBattleResults(b.battle_id)}>
                      <div className="flex items-center justify-between">
                        <div><p className="font-medium text-sm">{b.title}</p><p className="text-xs text-muted-foreground">{b.class_name} &middot; {b.participant_count} students</p></div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                      {b.top_3?.length > 0 && (
                        <div className="flex gap-3 mt-1">
                          {b.top_3.map((p, i) => (
                            <span key={i} className="text-xs text-muted-foreground">{["🥇","🥈","🥉"][i]} {p.name}: {p.score}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Reset Weekly Battle Leaderboard */}
              {classes.length > 0 && (
                <div className="p-3 rounded-xl border border-border bg-muted/10 space-y-2">
                  <h4 className="font-bold text-sm flex items-center gap-1"><RotateCcw className="w-4 h-4" />Reset Weekly Battle Board</h4>
                  <div className="flex flex-wrap gap-2">
                    {classes.map(c => (
                      <Button key={c.class_id} size="sm" variant="outline" data-testid={`reset-battle-weekly-${c.class_id}`}
                        onClick={async () => {
                          if (!confirm(`Reset weekly battle leaderboard for ${c.name}?`)) return;
                          try {
                            await axios.post(`${API}/arena/battle-leaderboard/weekly/reset/${c.class_id}`, {}, { headers });
                            toast.success(`${c.name} weekly battle board reset!`);
                          } catch { toast.error("Failed"); }
                        }}>
                        {c.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        )}


        {/* Classes Section */}
        <Card className="border-border/50">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
            <div>
              <CardTitle className="font-['Manrope']">My Classes</CardTitle>
              <CardDescription><CT k="teacher_welcome_subtitle" block /></CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid="open-media-library-btn"
                variant="outline"
                className="rounded-full"
                onClick={() => navigate("/media-library")}
              >
                <CT k="teacher_media_library_button" />
              </Button>
              <Button
                data-testid="open-secure-exams-btn"
                variant="outline"
                className="rounded-full"
                onClick={() => navigate("/secure-exams")}
              >
                <CT k="teacher_secure_exams_button" />
              </Button>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-class-btn" className="rounded-full bg-primary text-primary-foreground">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Class
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Create New Class</DialogTitle>
                  <DialogDescription>Create a new class for your students</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="class-name">Class Name *</Label>
                    <Input
                      data-testid="class-name-input"
                      id="class-name"
                      placeholder="e.g., Introduction to Physics"
                      value={newClass.name}
                      onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      data-testid="subject-input"
                      id="subject"
                      placeholder="e.g., Physics"
                      value={newClass.subject}
                      onChange={(e) => setNewClass({ ...newClass, subject: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      data-testid="description-input"
                      id="description"
                      placeholder="Brief description of the class"
                      value={newClass.description}
                      onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schedule">Schedule (Optional)</Label>
                    <Input
                      data-testid="schedule-input"
                      id="schedule"
                      placeholder="e.g., Mon, Wed 10:00 AM"
                      value={newClass.schedule}
                      onChange={(e) => setNewClass({ ...newClass, schedule: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="meet-link">Meeting Link (Optional)</Label>
                    <Input
                      data-testid="meet-link-input"
                      id="meet-link"
                      placeholder="https://meet.google.com/... or https://zoom.us/..."
                      value={newClass.meet_link}
                      onChange={(e) => setNewClass({ ...newClass, meet_link: e.target.value })}
                    />
                  </div>
                  <Button
                    data-testid="submit-create-btn"
                    onClick={handleCreateClass}
                    className="w-full rounded-full bg-primary text-primary-foreground"
                    disabled={creating}
                  >
                    {creating ? "Creating..." : "Create Class"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {classes.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-lg text-muted-foreground">No classes yet</p>
                <p className="text-sm text-muted-foreground mb-4">Create your first class to get started</p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="rounded-full bg-primary text-primary-foreground"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Class
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map((cls) => (
                  <motion.div
                    key={cls.class_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Card 
                      className="border-border/50 hover:border-primary/50 cursor-pointer transition-all card-hover"
                      onClick={() => navigate(`/class/${cls.class_id}`)}
                      data-testid={`class-card-${cls.class_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRenameDialog(cls);
                              }}
                              data-testid={`edit-class-${cls.class_id}`}
                              title="Edit Class"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyCode(cls.join_code);
                              }}
                              data-testid={`copy-code-${cls.class_id}`}
                            >
                              {copiedCode === cls.join_code ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                              <span className="ml-1 text-xs">{cls.join_code}</span>
                            </Button>
                          </div>
                        </div>
                        <h3 className="font-semibold text-foreground mb-1">{cls.name}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{cls.subject}</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {cls.student_count} students
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rename Class Dialog */}
        <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <Pencil className="w-5 h-5 text-primary" />
                Edit Class
              </DialogTitle>
              <DialogDescription>Update class name, subject, or description</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="rename-name">Class Name *</Label>
                <Input
                  data-testid="rename-class-name"
                  id="rename-name"
                  value={renameData.name}
                  onChange={(e) => setRenameData({ ...renameData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rename-subject">Subject</Label>
                <Input
                  data-testid="rename-class-subject"
                  id="rename-subject"
                  value={renameData.subject}
                  onChange={(e) => setRenameData({ ...renameData, subject: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rename-desc">Description</Label>
                <Textarea
                  data-testid="rename-class-description"
                  id="rename-desc"
                  value={renameData.description}
                  onChange={(e) => setRenameData({ ...renameData, description: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setRenameDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-rename-btn"
                  className="flex-1 rounded-full bg-primary text-primary-foreground"
                  onClick={handleRenameClass}
                  disabled={renaming}
                >
                  {renaming ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Arena Players dialog */}
        <Dialog open={showPlayers} onOpenChange={(o) => { setShowPlayers(o); if (!o) { setUpgradeTarget(null); setUpgradePassword(""); setUpgradeClassId(""); } }}>
          <DialogContent className="max-w-lg" data-testid="arena-players-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" />Arena Players</DialogTitle>
              <DialogDescription>
                {upgradeTarget ? "Enroll this player as a student. Their Arena coins transfer automatically and they'll get a welcome email with login details." : "Guest players who signed in to save their Times Tables Arena progress. Upgrade them into enrolled students with one click."}
              </DialogDescription>
            </DialogHeader>
            {upgradeTarget ? (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                  <div className="font-semibold">{upgradeTarget.name}</div>
                  <div className="text-xs text-muted-foreground">{upgradeTarget.email}</div>
                  <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1"><Coins className="w-3.5 h-3.5" />{upgradeTarget.progress?.coins || 0} coins will transfer to their student Arena profile</div>
                </div>
                <div>
                  <Label>Enroll into class</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1" value={upgradeClassId} onChange={(e) => setUpgradeClassId(e.target.value)} data-testid="upgrade-class-select">
                    <option value="">Select a class...</option>
                    {classes.map(c => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Set login password (min 6 chars)</Label>
                  <Input type="text" value={upgradePassword} onChange={(e) => setUpgradePassword(e.target.value)} placeholder="e.g. Student@123" data-testid="upgrade-password-input" className="mt-1" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => setUpgradeTarget(null)}>Back</Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                    disabled={upgrading || !upgradeClassId || upgradePassword.length < 6}
                    onClick={async () => {
                      setUpgrading(true);
                      try {
                        const r = await axios.post(`${API}/arena/players/${upgradeTarget.user_id}/upgrade`,
                          { class_id: upgradeClassId, password: upgradePassword },
                          { headers: { Authorization: `Bearer ${token}` } });
                        toast.success(`${r.data.name} is now a student in ${r.data.class_name}! ${r.data.coins_transferred} coins transferred.`);
                        setPlayers(prev => (prev || []).filter(p => p.user_id !== upgradeTarget.user_id));
                        setUpgradeTarget(null); setUpgradePassword(""); setUpgradeClassId("");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Upgrade failed");
                      } finally { setUpgrading(false); }
                    }}
                    data-testid="confirm-upgrade-btn"
                  >
                    {upgrading ? "Upgrading..." : "Upgrade to Student"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {players === null ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
                ) : players.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No Arena players yet. Share the free arena link — when guests sign in to save progress, they'll appear here.</p>
                ) : players.map(p => (
                  <div key={p.user_id} className="flex items-center gap-3 border border-border rounded-lg p-3" data-testid={`player-row-${p.user_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{p.progress?.nickname || p.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        <Coins className="w-3 h-3 inline text-yellow-500 mr-0.5" />{p.progress?.coins || 0} coins · ⚡{p.progress?.best_speed_score || 0} · 🎯{p.progress?.best_practice_score || 0} · {p.progress?.games_played || 0} games
                      </div>
                    </div>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0" onClick={() => setUpgradeTarget(p)} data-testid={`upgrade-${p.user_id}`}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" />Upgrade
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Question reports dialog */}
        <Dialog open={showReports} onOpenChange={setShowReports}>
          <DialogContent className="max-w-xl" data-testid="question-reports-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-rose-500" />Reported Problems</DialogTitle>
              <DialogDescription>Issues your students reported on exam questions. Fix the question in the exam builder, then mark the report resolved.</DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto space-y-3">
              {reports === null ? (
                <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
              ) : reports.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No reports yet — students can report question issues from inside the exam.</p>
              ) : reports.map(r => (
                <div key={r.report_id} className={`border rounded-lg p-3 ${r.resolved ? "border-border opacity-60" : "border-rose-200 bg-rose-50/40"}`} data-testid={`report-row-${r.report_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-muted-foreground">{r.exam_title} · {r.student_name} · {new Date(r.created_at).toLocaleString()}</div>
                      {r.question_text && <div className="text-sm font-medium mt-1 truncate">Q: {r.question_text}</div>}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(r.reasons || []).map((reason, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{reason}</span>
                        ))}
                      </div>
                      {r.explanation && <div className="text-xs text-slate-600 mt-1.5 italic">"{r.explanation}"</div>}
                      {r.question_id && (
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-xs text-sky-700 border-sky-300 hover:bg-sky-50"
                          onClick={() => { setShowReports(false); navigate(`/secure-exams/${r.exam_id}/edit?focus=${r.question_id}`); }}
                          data-testid={`edit-question-${r.report_id}`}>
                          Edit Question
                        </Button>
                      )}
                    </div>
                    {r.resolved ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold shrink-0">Resolved</span>
                    ) : (
                      <Button size="sm" variant="outline" className="shrink-0 text-green-700 border-green-300 hover:bg-green-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/secure-exam-reports/${r.report_id}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            setReports(prev => prev.map(x => x.report_id === r.report_id ? { ...x, resolved: true } : x));
                            toast.success("Marked resolved");
                          } catch (e) { toast.error("Failed to resolve"); }
                        }}
                        data-testid={`resolve-report-${r.report_id}`}>
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
